import { useAudiences } from '~/composables/useAudiences';
import {
  AudienceRuleBuilder,
  type SalesforceSla,
  UserData
} from '~/utils/rules/audiences';
import { type BracketSchema, type RuleSchema } from '~/utils/rules/base';

export type AudienceId = string;
export type AudienceEntry = {
  label: string;
  description: string;
  id: AudienceId;
  matches: boolean;
};
export type AudiencesMap = Record<AudienceId, AudienceEntry>;

export default defineNuxtPlugin(async () => {
  const { $requestedLocale } = useNuxtApp();

  const { loggedIn, user } = useOidcAuth();
  const route = useRoute();

  const allAudiences = await useAudiences($requestedLocale.value);
  const audiences: Ref<AudiencesMap> = ref<AudiencesMap>({});
  const matchingAudiences: Ref<AudienceId[]> = ref([]);

  watch(
    () => route.query,
    () => {
      audiences.value = (allAudiences.value?.entries ?? []).reduce(
        (reducer, audience) => {
          let matches = false;
          if (audience.fields.audienceDefinition) {
            // TODO: valibot for schema parsing
            matches = AudienceRuleBuilder.build(
              audience.fields.audienceDefinition as Readonly<
                RuleSchema | BracketSchema
              >
            ).matches(
              new UserData({
                isLoggedIn: loggedIn.value,
                salesforceSla: user.value?.claims?.sf_SLA as
                  | SalesforceSla
                  | undefined,
                queryParameters: route.query
              })
            );
          }

          return {
            ...reducer,
            [audience.sys.id]: {
              id: audience.sys.id,
              label: audience.fields.internalName,
              description: audience.fields.description,
              matches
            }
          };
        },
        {}
      );

      matchingAudiences.value.length = 0; // empty the array by not losing the reference
      matchingAudiences.value.push(
        ...Object.values(audiences.value)
          .filter((audience) => audience.matches)
          .map((audience) => audience.id)
      );
    },
    { immediate: true }
  );

  return {
    provide: {
      matchingAudiences
    }
  };
});
